import type { LoaderData } from '../Track/Track'

import { useEffect, useRef, useState } from 'react'
import { useLoaderData } from 'react-router-dom'

import msToTimestamp from '../../utils/msToTimestamp'
import timestampToMs from '../../utils/timestampToMs'

import SoundCloudWidget from '../SoundCloudWidget/SoundCloudWidget'

import './Player.css'

type Lyric = {
  ms: number,
  sentence: string,
}
type Lyrics = Array<Lyric>

export default function Player () {
  const { soundCloudTrackId, sentencesUrl } = useLoaderData() as LoaderData

  const currentLyricRef = useRef<HTMLElement>(null);
  const seekToRef = useRef<SoundCloud.SeekToFn | null>(null)

  const [lyrics, setLyrics] = useState<Lyrics>([])
  const [lyricIndex, setLyricIndex] = useState<number>(0)
  const [milliseconds, setMilliseconds] = useState<number>(0)

  useEffect(() => {
    fetch(sentencesUrl).then(async response => {
      const text = await response.text()
      const newLyrics: Lyrics = text.trim().split('\n').map(line => {
        line = line.trim()
        const indexOfFirstSpace = line.indexOf(' ')
        if (indexOfFirstSpace === -1) {
          return {
            ms: timestampToMs(line),
            sentence: '',
          }
        } else {
          const timestamp = line.substring(0, indexOfFirstSpace)
          const ms = timestampToMs(timestamp)
          const sentence = line.substring(indexOfFirstSpace + 1)
          return { ms, sentence }
        }
      }).filter(x => !!x.sentence) // TODO: temporarily filtering out the lyric entries with no words
      setLyrics(newLyrics)
    })
  }, [sentencesUrl])

  const onPlayProgress: SoundCloud.AudioEventCallback = ({ currentPosition }) => setMilliseconds(currentPosition)

  useEffect(() => {
    const nextLyricIndex = lyrics.findIndex(({ ms }) => ms >= milliseconds)
    if ( nextLyricIndex === -1 ) {
      setLyricIndex(lyrics.length - 1)
    } else {
      setLyricIndex(nextLyricIndex - 1)
    }
  }, [lyrics, milliseconds])

  useEffect(() => {
    currentLyricRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'end',
    })
  }, [lyricIndex])

  return (
    <div className='player'>
      <div className='widgetPane'>
        <SoundCloudWidget
          trackId={soundCloudTrackId}
          onPlayProgress={onPlayProgress}
          registerSeekTo={seekTo => seekToRef.current = seekTo}
        />
      </div>
      {!!lyrics.length && (
        <div className='lyricsPane'>
          {lyricIndex === -1 ? (
            <>
              <span key='timestamp0' className='timestamp' onClick={() => seekToRef.current?.(lyrics[0].ms)}>
                {msToTimestamp(lyrics[0].ms)}
              </span>
              <span key='sentence0' className='sentence'>{lyrics[0].sentence}</span>
            </>
          ) : (
            lyrics.slice(0, lyricIndex+1).map(({ ms, sentence }, index) => {
              const isActiveLyric = index === lyricIndex
              const specialProps = {
                ref: isActiveLyric ? currentLyricRef : undefined
              }
              return [
                <span key={`timestamp${index}`} className='timestamp' onClick={() => seekToRef.current?.(ms)}>
                  {msToTimestamp(ms)}
                </span>,
                <span key={`sentence${index}`} className={'sentence' + (isActiveLyric ? ' active' : '')} {...specialProps} >{sentence}</span>
              ]
            }).flat()
          )}
        </div>
      )}
    </div>
  )
}
